/* ============================================================================
   Registro de secciones de la hoja de consulta y modelo de avance.

   TRES niveles (decisión del cliente 2026-09-09):
   - OBLIGATORIA  -> bloquea «Finalizar atención». La barra de avance mide SOLO
     estas cuatro.
   - RECOMENDADA  -> avisa al finalizar, nunca bloquea.
   - OPCIONAL     -> nunca avisa ni bloquea.

   El orden del array es el orden en que se apilan las secciones en la página,
   el de la franja de avance y el que siguen «‹ anterior» / «siguiente ›» y los
   atajos Alt+↑ / Alt+↓.

   ORDEN = el del submenú original (CONSULTA · SIGNOS VITALES · ENFERMEDAD
   ACTUAL · DIAGNOSTICOS · PLANES DE TRATAMIENTO · NOTA DE EVOLUCION · RECETAS ·
   EXAMENES · CERTIFICADOS), que es el orden clínico con el que el médico ya
   trabaja. Las dos secciones que llegan desde Antecedentes (revisión por
   sistemas y examen físico) se intercalan donde corresponde clínicamente:
   después de la enfermedad actual y antes de los diagnósticos.

   El nivel de exigencia NO define el orden: se comunica con el marcador de
   cada pastilla, el chip de la cabecera de cada sección, la barra de progreso
   (que mide solo las obligatorias) y el resumen al finalizar.
   ========================================================================== */

export const NIVEL = {
  OBLIGATORIA: "OBLIGATORIA",
  RECOMENDADA: "RECOMENDADA",
  OPCIONAL: "OPCIONAL",
};

export const GRUPOS = [
  { nivel: NIVEL.OBLIGATORIA, etiqueta: "Obligatorias", sufijo: "bloquean" },
  { nivel: NIVEL.RECOMENDADA, etiqueta: "Recomendadas · avisan, no bloquean" },
  { nivel: NIVEL.OPCIONAL, etiqueta: "Según el caso" },
];

// `corto` se usa en el rail horizontal (franja compacta previa a abrir la
// consulta), donde el ancho manda; el `titulo` completo se sigue usando en el
// rail lateral, en la cabecera de cada sección y en los avisos.
export const SECCIONES = [
  { id: "motivo", titulo: "Motivo de consulta", corto: "Motivo", nivel: NIVEL.RECOMENDADA },
  { id: "signos", titulo: "Signos vitales", corto: "Signos", nivel: NIVEL.RECOMENDADA },
  { id: "enfermedad", titulo: "Enfermedad actual", corto: "Enfermedad", nivel: NIVEL.OBLIGATORIA },
  { id: "sistemas", titulo: "Revisión por sistemas", corto: "Sistemas", nivel: NIVEL.RECOMENDADA },
  { id: "fisico", titulo: "Examen físico", corto: "Ex. físico", nivel: NIVEL.RECOMENDADA },
  { id: "diagnosticos", titulo: "Diagnósticos", corto: "Diagnósticos", nivel: NIVEL.OBLIGATORIA },
  { id: "plan", titulo: "Plan de tratamiento", corto: "Plan", nivel: NIVEL.OBLIGATORIA },
  { id: "evolucion", titulo: "Nota de evolución", corto: "Evolución", nivel: NIVEL.OBLIGATORIA },
  { id: "recetas", titulo: "Recetas", corto: "Recetas", nivel: NIVEL.OPCIONAL },
  { id: "ordenes", titulo: "Órdenes", corto: "Órdenes", nivel: NIVEL.OPCIONAL },
  { id: "certificados", titulo: "Certificados", corto: "Certificados", nivel: NIVEL.OPCIONAL },
];

export const anchorId = (id) => `cev-sec-${id}`;

const txt = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * Deriva el estado de avance de cada sección a partir del modelo en memoria.
 * NO llama al servidor. `data` es el shape del orquestador (ver ConsultaEvolucion).
 *
 * Devuelve, por id de sección:
 *   estado: "completa" | "parcial" | "vacia"
 *   detalle: texto corto para el rail / pie ("2", "9 de 10", "falta")
 */
export function evaluarAvance(data) {
  const d = data || {};
  const r = {};

  r.enfermedad = boolSec(txt(d.enfermedad) !== "");
  r.plan = boolSec(txt(d.plan) !== "");
  r.evolucion = boolSec(txt(d.evolucion) !== "");
  r.motivo = boolSec(txt(d.motivo) !== "");

  const nDx = (d.diagnosticos || []).length;
  r.diagnosticos = {
    estado: nDx > 0 ? "completa" : "vacia",
    detalle: nDx > 0 ? String(nDx) : "falta",
  };

  const sv = d.signos || {};
  const svCampos = ["temperatura", "pulso", "presion", "frecuencia_respiratoria", "saturacion_oxigeno", "peso", "talla"];
  const svLlenos = svCampos.filter((k) => txt(String(sv[k] ?? "")) !== "").length;
  r.signos = {
    estado: svLlenos === 0 ? "vacia" : svLlenos >= 3 ? "completa" : "parcial",
    detalle: sv.origen_recepcion ? "recepción" : svLlenos > 0 ? `${svLlenos} campos` : "falta",
  };

  r.sistemas = contarHallazgos(d.sistemas);
  r.fisico = contarHallazgos(d.fisico);

  const nOrd = (d.ordenes || []).length;
  r.ordenes = { estado: nOrd > 0 ? "completa" : "vacia", detalle: nOrd > 0 ? String(nOrd) : "—" };
  const nRec = (d.recetas || []).length;
  r.recetas = { estado: nRec > 0 ? "completa" : "vacia", detalle: nRec > 0 ? String(nRec) : "—" };
  const nCert = (d.certificados || []).length;
  r.certificados = { estado: nCert > 0 ? "completa" : "vacia", detalle: nCert > 0 ? String(nCert) : "—" };

  return r;
}

function boolSec(ok) {
  return { estado: ok ? "completa" : "vacia", detalle: ok ? "" : "falta" };
}

function contarHallazgos(items) {
  const list = items || [];
  if (list.length === 0) return { estado: "vacia", detalle: "—" };
  const evaluados = list.filter((x) => x.cp === true || x.sp === true).length;
  return {
    estado: evaluados === list.length ? "completa" : evaluados === 0 ? "vacia" : "parcial",
    detalle: `${evaluados}/${list.length}`,
  };
}

/** ¿Se puede pulsar «Finalizar atención»? Solo las 4 obligatorias cuentan. */
export function evaluarFinalizacion(avance) {
  const obligatorias = SECCIONES.filter((s) => s.nivel === NIVEL.OBLIGATORIA);
  const bloqueos = obligatorias.filter((s) => avance[s.id]?.estado !== "completa");
  const avisos = SECCIONES.filter(
    (s) => s.nivel === NIVEL.RECOMENDADA && ["vacia", "parcial"].includes(avance[s.id]?.estado)
  );
  return {
    puedeFinalizar: bloqueos.length === 0,
    bloqueos, // secciones OBLIGATORIA incompletas
    avisos, // secciones RECOMENDADA incompletas
  };
}

/** Progreso 0..1 medido SOLO sobre las obligatorias. */
export function progresoObligatorias(avance) {
  const obl = SECCIONES.filter((s) => s.nivel === NIVEL.OBLIGATORIA);
  const hechas = obl.filter((s) => avance[s.id]?.estado === "completa").length;
  return { hechas, total: obl.length, ratio: obl.length ? hechas / obl.length : 0 };
}

/** Siguiente sección con pendientes (obligatoria o recomendada), o null. */
export function siguientePendiente(avance, desdeId) {
  const idx = SECCIONES.findIndex((s) => s.id === desdeId);
  const orden = [...SECCIONES.slice(idx + 1), ...SECCIONES.slice(0, idx + 1)];
  const pend = orden.find(
    (s) =>
      s.nivel !== NIVEL.OPCIONAL &&
      ["vacia", "parcial"].includes(avance[s.id]?.estado)
  );
  return pend ? pend.id : null;
}
